import type React from "react";

import { twMerge } from "tailwind-merge";

import { Button, type Color } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
} from "@/components/dialog";
import { Modal } from "@/components/modal";

export function ConfirmationModal({
  cancelButtonText = "Cancel",
  children,
  className = "w-[400px]",
  confirmButtonColor = "destructive",
  confirmButtonText = "Confirm",
  icon,
  iconBackgroundClassName = "bg-bg-error-secondary",
  isConfirmLoading = false,
  isOpen,
  modalOverlayClassName,
  onConfirm,
  onOpenChange,
  text,
  title,
}: ConfirmationModalProps) {
  return (
    <Modal
      classNames={{
        modal: twMerge("flex flex-col", className),
        modalOverlay: modalOverlayClassName,
      }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <Dialog className="flex-1">
        <DialogBody>
          {icon && (
            <div
              className={twMerge(
                "grid size-6xl shrink-0 place-items-center rounded-full",
                iconBackgroundClassName,
              )}
            >
              {icon}
            </div>
          )}
          <div className="flex flex-col gap-xs">
            <span className="text-lg font-semibold text-text-primary">
              {title}
            </span>
            <DialogCloseButton />
            {typeof text === "string" ? (
              <p className="text-sm text-text-tertiary">{text}</p>
            ) : (
              text
            )}
            {children}
          </div>
        </DialogBody>
        <DialogFooter>
          <DialogCloseButton>{cancelButtonText}</DialogCloseButton>
          <Button
            color="gray"
            onPress={() => onOpenChange(false)}
            size="lg"
            variant="secondary"
          >
            {cancelButtonText}
          </Button>
          <Button
            color={confirmButtonColor}
            isDisabled={isConfirmLoading}
            onPress={onConfirm}
            size="lg"
            variant="primary"
          >
            {isConfirmLoading ? "Processing..." : confirmButtonText}
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

export interface ConfirmationModalProps {
  /**
   * The text for the cancel button
   * @default "Cancel"
   */
  cancelButtonText?: string;
  /**
   * Additional content to render in the modal body
   */
  children?: React.ReactNode;
  /**
   * Custom className for the modal
   * @default "w-[400px]"
   */
  className?: string;
  /**
   * The color of the confirm button
   * @default "destructive"
   */
  confirmButtonColor?: Color;
  /**
   * The text for the confirm button
   * @default "Confirm"
   */
  confirmButtonText?: string;
  /**
   * The icon to display in the modal
   */
  icon?: React.ReactNode;
  /**
   * Tailwind class controlling the background colour of the round icon
   * container. Defaults to a destructive-red background to match the most
   * common (delete) usage.
   * @default "bg-bg-error-secondary"
   */
  iconBackgroundClassName?: string;
  /**
   * Whether the confirm button is in a loading state
   */
  isConfirmLoading?: boolean;
  /**
   * Whether the modal is open
   */
  isOpen: boolean;
  /**
   * Custom className for the modal overlay
   */
  modalOverlayClassName?: string;
  /**
   * Callback when the confirm button is pressed
   */
  onConfirm: () => void;
  /**
   * Callback when the modal open state changes
   */
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * The text content of the modal
   */
  text?: React.ReactNode | string;
  /**
   * The title of the modal
   */
  title: string;
}
